import type { ControllerPermission } from '@/hooks/usePermission';
import { getOrgScopeModulePermission, mapRoleToPermission, pickPrimaryModuleRole } from '@/hooks/usePermission';
import type { IDepartmentViewModel, IEmployeeViewModel } from '@/types/employee';

export type OrgScope = {
  useFullOrg: boolean;
  treatAsQuanLy: boolean;
  roleVuotCap: boolean;
  roleNgangCap: boolean;
};

const BUOC_VUOT_CAP_CONTROLLERS = new Set(['buoc5', 'buoc6', 'buoc11', 'buoc11dashboard']);

function resolveRoleVuotCap(
  profile: any,
  modulePerm: ControllerPermission | null | undefined,
  controller: string | null | undefined,
  moduleScopedAdmin: boolean,
): boolean {
  if (moduleScopedAdmin) return false;
  if (modulePerm?.allCT) return true;

  if (controller && BUOC_VUOT_CAP_CONTROLLERS.has(controller)) {
    const listRole = profile?.listRole;
    if (Array.isArray(listRole)) {
      const vuotCapRole = pickPrimaryModuleRole(listRole, 'buoc5_vuotcap');
      if (vuotCapRole && mapRoleToPermission(vuotCapRole).read) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Gộp quyền global (profile) và quyền module (listRole) để lọc cơ cấu tổ chức.
 * - profile.isAdmin: toàn bộ cơ cấu
 * - module fullcontrol (admin trực thuộc): BP của user + phòng ban con + nhân viên (giống quản lý)
 */
export function resolveOrgScope(
  profile: any,
  modulePerm?: ControllerPermission | null,
  controller?: string | null,
): OrgScope {
  const useFullOrg = Boolean(profile?.isAdmin);
  const moduleScopedAdmin = Boolean(!useFullOrg && modulePerm?.fullcontrol);

  if (useFullOrg) {
    return {
      useFullOrg: true,
      treatAsQuanLy: true,
      roleVuotCap: true,
      roleNgangCap: true,
    };
  }

  const normalizedController = controller ?? modulePerm?.controller ?? null;

  return {
    useFullOrg: false,
    treatAsQuanLy: Boolean(profile?.isQuanLy || modulePerm?.quanLy || moduleScopedAdmin),
    roleVuotCap: resolveRoleVuotCap(profile, modulePerm, normalizedController, moduleScopedAdmin),
    roleNgangCap: moduleScopedAdmin ? false : Boolean(modulePerm?.allBP),
  };
}

export function normalizeModuleController(controller: string | null | undefined): string | null {
  if (!controller) return null;
  if (controller === 'buoc11') return 'buoc6';
  return controller;
}

/** Bộ phận loại công ty (Key=1 / IsCompany / Loại). */
export function isCompanyDepartment(dept: any): boolean {
  if (!dept) return false;
  if (dept.Key == 1 || dept.IsCompany === true || dept.isCompany === true) return true;
  const loai = String(dept.Loai ?? dept.loai ?? dept.TenLoai ?? dept.tenLoai ?? '')
    .trim()
    .toLowerCase();
  return loai === 'công ty' || loai === 'cong ty' || loai.includes('cong ty');
}

/** Tìm công ty gần nhất từ bộ phận user đi lên cây (không dừng ở phòng ban con). */
export function findAncestorCompanyDepartment(
  deptId: string | null | undefined,
  departments: any[],
): any | null {
  if (!deptId || !departments?.length) return null;

  const byId = new Map<string, any>();
  departments.forEach((d) => {
    const id = String(d?.ID ?? d?.id ?? '');
    if (id) byId.set(id, d);
  });

  let current = byId.get(String(deptId));
  const visited = new Set<string>();

  while (current) {
    const id = String(current.ID ?? current.id ?? '');
    if (!id || visited.has(id)) break;
    visited.add(id);

    const parentId = String(current.ParentID ?? current.parentID ?? '0');
    const isRoot = !parentId || parentId === '0';

    // Bỏ qua chính bộ phận user nếu chưa phải công ty — tìm công ty ở cấp cha
    if (!isRoot && isCompanyDepartment(current)) {
      return current;
    }

    if (isRoot && isCompanyDepartment(current)) {
      return current;
    }

    if (isRoot) break;
    current = byId.get(parentId);
  }

  return null;
}

/** Các bộ phận loại công ty là con/cháu của anchor (không gồm anchor). */
export function getSubordinateCompanyDepartments(
  anchorDeptId: string | null | undefined,
  departments: any[],
): any[] {
  if (!anchorDeptId || !departments?.length) return [];

  const childrenByParent = new Map<string, any[]>();
  departments.forEach((d) => {
    const id = String(d?.ID ?? d?.id ?? '');
    const pId = String(d?.ParentID ?? d?.parentID ?? '0');
    if (!id) return;
    if (!childrenByParent.has(pId)) childrenByParent.set(pId, []);
    childrenByParent.get(pId)!.push(d);
  });

  const result: any[] = [];
  const queue = [...(childrenByParent.get(String(anchorDeptId)) ?? [])];
  const visited = new Set<string>();

  while (queue.length) {
    const dept = queue.shift()!;
    const id = String(dept.ID ?? dept.id ?? '');
    if (!id || visited.has(id)) continue;
    visited.add(id);

    if (isCompanyDepartment(dept)) {
      result.push(dept);
    }

    queue.push(...(childrenByParent.get(id) ?? []));
  }

  return result;
}

/** Chuẩn hóa pId trong tập công ty được phép hiển thị trên cây VH. */
export function remapCompanyTreeParentId(
  dept: any,
  allowedIds: Set<string>,
  departments: any[],
): string {
  let pId = String(dept.ParentID ?? dept.parentID ?? '0');
  const byId = new Map<string, any>();
  departments.forEach((d) => {
    const id = String(d?.ID ?? d?.id ?? '');
    if (id) byId.set(id, d);
  });

  while (pId && pId !== '0' && !allowedIds.has(pId)) {
    const parent = byId.get(pId);
    pId = String(parent?.ParentID ?? parent?.parentID ?? '0');
  }
  return allowedIds.has(pId) ? pId : '0';
}

export function computeSubOrganization(
  profile: any,
  departments: IDepartmentViewModel[],
  employees: IEmployeeViewModel[],
  roleVuotCap: boolean,
  roleNgangCap: boolean,
  options?: { treatAsQuanLy?: boolean; useFullOrg?: boolean }
): { departments: IDepartmentViewModel[]; employees: IEmployeeViewModel[] } {
  const treatAsQuanLy = Boolean(options?.treatAsQuanLy);
  const useFullOrg = Boolean(options?.useFullOrg || profile?.isAdmin);

  if (useFullOrg) {
    return { departments, employees };
  }

  if (!profile?.isQuanLy && !treatAsQuanLy) {
    const member = employees.find((emp) => {
      const empId = emp.id || (emp as any).ID || '';
      return empId === profile?.id;
    });

    if (!member) {
      return { departments: [], employees: [] };
    }

    const bpId = profile?.boPhanId;

    if (!roleVuotCap && !roleNgangCap) {
      return { departments: [], employees: [member] };
    }

    let memberDepartments: IDepartmentViewModel[] = [];
    if (roleVuotCap && bpId) {
      const dept = departments.find((d) => {
        const deptId = (d as any).ID;
        return deptId === bpId;
      });
      if (dept) memberDepartments = [dept];
    }

    let memberEmployees: IEmployeeViewModel[] = [];
    if (roleNgangCap && bpId) {
      memberEmployees = employees.filter((emp) => emp.BPID === bpId);
    } else {
      memberEmployees = [member];
    }

    return { departments: memberDepartments, employees: memberEmployees };
  }

  if (profile?.isQuanLy || treatAsQuanLy) {
    const bpId = profile?.boPhanId;
    if (!bpId) {
      const member = employees.find((emp) => {
        const empId = emp.id || (emp as any).ID || '';
        return empId === profile?.id;
      });
      return { departments: [], employees: member ? [member] : [] };
    }

    const currentDepartment = departments.find((dept) => {
      const deptId = dept.id || (dept as any).ID || '';
      return deptId === bpId;
    });

    const currentParentId = currentDepartment
      ? (currentDepartment as any).parentID || (currentDepartment as any).ParentID || ''
      : '';

    const getAllSubDepartmentIds = (parentId: string): Set<string> => {
      const subDeptIds = new Set<string>([parentId]);
      const findChildren = (deptId: string) => {
        departments.forEach((dept) => {
          const deptParentId = (dept as any).parentID || (dept as any).ParentID || '';
          if (deptParentId === deptId) {
            const deptIdValue = dept.id || (dept as any).ID || '';
            if (deptIdValue && !subDeptIds.has(deptIdValue)) {
              subDeptIds.add(deptIdValue);
              findChildren(deptIdValue);
            }
          }
        });
      };
      findChildren(parentId);
      return subDeptIds;
    };

    const allowedDepartmentIdsForEmployees = getAllSubDepartmentIds(bpId);
    const allowedDepartmentIdsForDepartments = new Set(allowedDepartmentIdsForEmployees);

    if (roleVuotCap && currentParentId) {
      allowedDepartmentIdsForDepartments.add(currentParentId);
    }

    if (roleNgangCap && currentParentId) {
      departments.forEach((dept) => {
        const deptId = dept.id || (dept as any).ID || '';
        const deptParentId = (dept as any).parentID || (dept as any).ParentID || '';
        if (deptParentId === currentParentId && deptId && deptId !== bpId) {
          allowedDepartmentIdsForDepartments.add(deptId);
        }
      });
    }

    return {
      departments: departments.filter((dept) => {
        const deptId = dept.id || (dept as any).ID || '';
        return allowedDepartmentIdsForDepartments.has(deptId);
      }),
      employees: employees.filter((emp) => {
        const empBpId = emp.bpid || (emp as any).BPID || '';
        return allowedDepartmentIdsForEmployees.has(empBpId);
      }),
    };
  }

  return { departments: [], employees: [] };
}

export function isIdInScopedOrg(
  idSelected: string,
  scoped: { departments: any[]; employees: any[] }
): boolean {
  const id = String(idSelected ?? '').trim();
  if (!id) return false;

  const inDept = scoped.departments.some((dept) => {
    const deptId = String(dept?.ID ?? dept?.id ?? dept?.Key ?? '').trim();
    return deptId === id;
  });
  if (inDept) return true;

  return scoped.employees.some((emp) => {
    const empId = String(emp?.ID ?? emp?.id ?? '').trim();
    return empId === id;
  });
}

export type TreeSelectSnapshot = {
  idSelect?: string | null;
  isEmployee?: boolean;
  isCompany?: boolean;
  parentID?: string | null;
  selectedValue?: { value?: string; label?: string } | null;
};

export type ResolvedTreeSelection = {
  selectedValue: { value: string; label: string };
  idSelect: string;
  isEmployee: boolean;
  parentID: string;
  isCompany: boolean;
};

export function getScopedOrganizationFromArrays(
  profile: any,
  departments: any[],
  employees: any[],
  orgScope: OrgScope,
) {
  if (orgScope.useFullOrg) {
    return { departments, employees };
  }
  return computeSubOrganization(
    profile,
    departments as IDepartmentViewModel[],
    employees as IEmployeeViewModel[],
    orgScope.roleVuotCap,
    orgScope.roleNgangCap,
    { treatAsQuanLy: orgScope.treatAsQuanLy },
  );
}

export function isTreeSelectInScopedOrg(
  selection: TreeSelectSnapshot,
  scoped: { departments: any[]; employees: any[] },
): boolean {
  const idSelect = String(selection.idSelect ?? '').trim();
  const rawValue = String(selection.selectedValue?.value ?? '');
  if (!idSelect && !rawValue) return false;

  if (selection.isEmployee || rawValue.startsWith('emp_')) {
    const empId = rawValue.startsWith('emp_')
      ? (rawValue.split('_dep_')[0]?.replace(/^emp_/, '') || idSelect)
      : idSelect;
    return isIdInScopedOrg(empId, scoped);
  }

  const deptId = rawValue && !rawValue.startsWith('emp_') ? rawValue : idSelect;
  return isIdInScopedOrg(deptId, scoped);
}

export function buildProfileDefaultTreeSelection(
  profile: any,
  departments: any[],
  orgScope: OrgScope,
  options?: { isShowEmployee?: boolean },
): ResolvedTreeSelection | null {
  if (!profile?.boPhanId) return null;

  const bpOri = departments.find((d) => String(d.ID ?? d.id) === String(profile.boPhanId));
  if (!bpOri) return null;

  const useEmployeeNode =
    !orgScope.useFullOrg &&
    !orgScope.treatAsQuanLy &&
    Boolean(profile.id) &&
    options?.isShowEmployee !== false;

  if (useEmployeeNode) {
    const empId = String(profile.id);
    return {
      selectedValue: {
        value: `emp_${empId}_dep_${profile.boPhanId}`,
        label: profile.tenNhanVien || '',
      },
      idSelect: empId,
      isEmployee: true,
      parentID: String(bpOri.ParentID ?? bpOri.parentID ?? '0'),
      isCompany: false,
    };
  }

  const deptId = String(bpOri.ID ?? bpOri.id ?? '');
  return {
    selectedValue: {
      value: deptId,
      label: String(bpOri.Ten ?? bpOri.ten ?? ''),
    },
    idSelect: deptId,
    isEmployee: false,
    parentID: String(bpOri.ParentID ?? bpOri.parentID ?? '0'),
    isCompany: isCompanyDepartment(bpOri),
  };
}

/** null = giữ selection hiện tại; object = reset theo quyền module / profile. */
export function resolveTreeSelectionForModule(
  profile: any,
  current: TreeSelectSnapshot,
  departments: any[],
  employees: any[],
  orgScope: OrgScope,
  options?: { isShowEmployee?: boolean },
): ResolvedTreeSelection | null {
  if (orgScope.useFullOrg) {
    return null;
  }

  const scoped = getScopedOrganizationFromArrays(profile, departments, employees, orgScope);

  if (
    current?.idSelect &&
    current?.selectedValue?.value &&
    isTreeSelectInScopedOrg(current, scoped)
  ) {
    return null;
  }

  return buildProfileDefaultTreeSelection(profile, departments, orgScope, options);
}
